import { Box, Stack, styled } from '@mui/material';
import { BREAKPOINTS, CHART_HEADER_CONSTANTS } from '../constants';

/**
 * Main Chart Header Container
 * Contains the entire chart header with responsive layout
 */
export const ChartHeaderContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(CHART_HEADER_CONSTANTS.CONTAINER_PADDING),
  marginBottom: theme.spacing(CHART_HEADER_CONSTANTS.CONTAINER_MARGIN_BOTTOM),
  flexWrap: 'nowrap',
  gap: 0,

  [theme.breakpoints.down(BREAKPOINTS.MOBILE_DESKTOP)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(CHART_HEADER_CONSTANTS.RESPONSIVE_GAP.xs),
  },

  [theme.breakpoints.up(BREAKPOINTS.MOBILE_DESKTOP)]: {
    gap: CHART_HEADER_CONSTANTS.RESPONSIVE_GAP.md,
  },
}));

/**
 * Title Section Container
 * Contains the chart title
 */
export const TitleContainer = styled(Stack)(({ theme }) => ({
  flex: '0 0 auto',
  [theme.breakpoints.down(BREAKPOINTS.MOBILE_DESKTOP)]: {
    display: 'none',
  },
}));

/**
 * Metrics Section Container
 * Contains all performance metrics with responsive layout
 * Desktop only - hidden on mobile
 */
export const MetricsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(CHART_HEADER_CONSTANTS.METRICS_GAP),
  flex: '1 1 auto',
  order: 2,
  paddingLeft: theme.spacing(CHART_HEADER_CONSTANTS.METRICS_PADDING.md),
  paddingRight: theme.spacing(CHART_HEADER_CONSTANTS.METRICS_PADDING.md),

  [theme.breakpoints.down(BREAKPOINTS.MOBILE_DESKTOP)]: {
    display: 'none',
  },
}));

/**
 * Mobile Layout Grid Container
 * Uses CSS Grid for precise alignment on mobile
 * Mobile only - hidden on desktop
 */
export const MobileLayoutGrid = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down(BREAKPOINTS.MOBILE_DESKTOP)]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto auto',
    width: '100%',
    gap: `${theme.spacing(CHART_HEADER_CONSTANTS.RESPONSIVE_GAP.xs)} ${theme.spacing(2)}`,
    alignItems: 'start',
  },
  [theme.breakpoints.up(BREAKPOINTS.MOBILE_DESKTOP)]: {
    display: 'none',
  },
}));

/**
 * Mobile Grid Cell - PNL (Top Left)
 */
export const MobileGridCellPnL = styled(Stack)({
  gridColumn: '1',
  gridRow: '1',
});

/**
 * Mobile Grid Cell - Dropdown (Top Right)
 */
export const MobileGridCellDropdown = styled(Stack)({
  gridColumn: '2',
  gridRow: '1',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
});

/**
 * Mobile Grid Cell - Equity (Bottom Left)
 */
export const MobileGridCellEquity = styled(Stack)({
  gridColumn: '1',
  gridRow: '2',
});

/**
 * Mobile Grid Cell - Closed Balance (Bottom Right)
 */
export const MobileGridCellClosedBalance = styled(Stack)({
  gridColumn: '2',
  gridRow: '2',
});

/**
 * Dropdown Section Container
 * Contains the filter dropdown
 * Desktop only - hidden on mobile (shown in MobilePnLRowContainer instead)
 */
export const DropdownContainer = styled(Stack)(({ theme }) => ({
  flex: '0 0 auto',
  order: 3,

  [theme.breakpoints.down(BREAKPOINTS.MOBILE_DESKTOP)]: {
    display: 'none',
  },
}));

/**
 * Mobile Dropdown Container
 * For dropdown used in mobile layout - no hiding on mobile
 */
export const MobileDropdownContainer = styled(Stack)({
  flex: '0 0 auto',
});

/**
 * Individual Metric Item Container
 * Contains label and value for each metric
 */
export const MetricItemContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(CHART_HEADER_CONSTANTS.METRIC_ITEM_GAP),
}));
